import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useMemo } from 'react';
import { useIsExternalTenant } from '../../hooks/useIsExternalTenant';
import NewBookingModal from './NewBookingModal';
import axios from 'axios';
import { API } from '../../utils/config';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import QuesturaTimer from '../../components/QuesturaTimer';
import { toast } from 'sonner';
import { ConfirmAction } from '../../components/home-smart/ConfirmAction';
import { CancelledItems } from '../../components/home-smart/CancelledItems';
import { cancellationApi, useCancelled } from '../../utils/homeSmartCancellations';
import { hsError } from '../../utils/homeSmartCollaborators';
import { Ban } from 'lucide-react';
import { 
  History, 
  Calendar,
  Users,
  Home,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  X,
  Phone,
  Mail,
  Building
} from 'lucide-react';

const StoricoPrenotazioni = () => {
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [properties, setProperties] = useState([]);
  const [expandedBooking, setExpandedBooking] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const isExternal = useIsExternalTenant();
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [view, setView] = useState('active');
  const cancelled = useCancelled(isExternal);

  const cancelBooking = async (booking) => {
    try {
      await cancellationApi.cancel('bookings', booking.id);
      toast.success('Prenotazione annullata: la trovi nella scheda «Annullate». Nessuna email è stata inviata all’ospite.');
      setExpandedBooking(null);
      setBookings((current) => current.filter((b) => b.id !== booking.id));
      await cancelled.reload();
    } catch (error) {
      toast.error(hsError(error));
      return false;
    }
  };

  const refreshAfterRestore = () => Promise.all([fetchData(true), cancelled.reload()]);
  
  // Filtri
  const [filters, setFilters] = useState({
    year: new Date().getFullYear().toString(),
    month: 'all',
    search: '',
    propertyId: 'all',
    dateFrom: '',
    dateTo: ''
  });
  
  // Filtri inline colonne
  const [columnFilters, setColumnFilters] = useState({
    guest_name: '',
    property: '',
    check_in: '',
    check_out: ''
  });
  
  // Ordinamento
  const [sortConfig, setSortConfig] = useState({
    field: 'check_in',
    direction: 'desc'
  });

  // Anni disponibili (corrente + 5 anni indietro)
  const currentYear = new Date().getFullYear();
  const availableYears = Array.from({ length: 8 }, (_, i) => (currentYear + 2 - i).toString());
  
  // Mesi
  const months = [
    { value: 'all', label: 'Tutti i mesi' },
    { value: '0', label: 'Gennaio' },
    { value: '1', label: 'Febbraio' },
    { value: '2', label: 'Marzo' },
    { value: '3', label: 'Aprile' },
    { value: '4', label: 'Maggio' },
    { value: '5', label: 'Giugno' },
    { value: '6', label: 'Luglio' },
    { value: '7', label: 'Agosto' },
    { value: '8', label: 'Settembre' },
    { value: '9', label: 'Ottobre' },
    { value: '10', label: 'Novembre' },
    { value: '11', label: 'Dicembre' }
  ];

  useEffect(() => {
    fetchData();
    // Segna come "viste" le prenotazioni per azzerare il badge
    localStorage.setItem('hw_last_viewed_bookings', new Date().toISOString());
  }, []);

  const fetchData = async (silent = false) => {
    try {
      if (silent !== true) setLoading(true);
      const token = localStorage.getItem('hw_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all bookings for responsible (include past for history)
      const bookingsRes = await axios.get(`${API}/api/responsible/my-bookings?include_past=true`, { headers });
      setBookings(bookingsRes.data || []);

      // Fetch properties
      const propsRes = await axios.get(`${API}/api/responsible/my-properties`, { headers });
      // Filter out properties without valid id
      const validProperties = (propsRes.data || []).filter(p => p && p.id);
      setProperties(validProperties);

    } catch (error) {
      console.error('Error fetching data:', error);
      // Fallback to general bookings if responsible endpoint fails
      try {
        const token = localStorage.getItem('hw_token');
        const headers = { Authorization: `Bearer ${token}` };
        const bookingsRes = await axios.get(`${API}/api/bookings`, { headers });
        setBookings(bookingsRes.data || []);
      } catch (e) {
        console.error('Fallback also failed:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper per ottenere il nome della proprietà
  const getPropertyName = (booking) => {
    if (booking.property_id) {
      const prop = properties.find(p => p.id === booking.property_id);
      if (prop) return prop.name;
    }
    return booking.property_name || 'N/A';
  };

  // Filtra e ordina le prenotazioni
  const filteredAndSortedBookings = useMemo(() => {
    let result = [...bookings];
    
    // Filtra solo prenotazioni passate (check_out < oggi) — per i tenant esterni mostra anche le future
    if (!isExternal) {
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      result = result.filter(b => new Date(b.check_out) < today);
    }
    
    // Filtro per anno
    if (filters.year !== 'all') {
      const year = parseInt(filters.year);
      result = result.filter(b => {
        const checkIn = new Date(b.check_in);
        return checkIn.getFullYear() === year;
      });
    }
    
    // Filtro per mese
    if (filters.month !== 'all') {
      const month = parseInt(filters.month);
      result = result.filter(b => {
        const checkIn = new Date(b.check_in);
        return checkIn.getMonth() === month;
      });
    }
    
    // Filtro per property
    if (filters.propertyId !== 'all') {
      result = result.filter(b => b.property_id === filters.propertyId);
    }
    
    // Filtro per data check-in (da)
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      result = result.filter(b => new Date(b.check_in) >= fromDate);
    }
    
    // Filtro per data check-in (a)
    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999);
      result = result.filter(b => new Date(b.check_in) <= toDate);
    }
    
    // Ricerca testuale (nome, cognome, telefono, email)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase().trim();
      result = result.filter(b => {
        const guestName = (b.guest_name || '').toLowerCase();
        const phone = (b.guest_phone || b.phone || '').toLowerCase();
        const email = (b.guest_email || b.email || '').toLowerCase();
        const bookingCode = (b.booking_code || '').toLowerCase();
        
        return guestName.includes(searchLower) ||
               phone.includes(searchLower) ||
               email.includes(searchLower) ||
               bookingCode.includes(searchLower);
      });
    }
    
    // Filtri inline colonne
    if (columnFilters.guest_name.trim()) {
      const searchLower = columnFilters.guest_name.toLowerCase().trim();
      result = result.filter(b => (b.guest_name || '').toLowerCase().includes(searchLower));
    }
    
    if (columnFilters.property.trim()) {
      const searchLower = columnFilters.property.toLowerCase().trim();
      result = result.filter(b => {
        const propName = getPropName(b).toLowerCase();
        return propName.includes(searchLower);
      });
    }
    
    if (columnFilters.check_in) {
      const filterDate = columnFilters.check_in;
      result = result.filter(b => {
        const checkIn = b.check_in ? new Date(b.check_in).toISOString().split('T')[0] : '';
        return checkIn.includes(filterDate);
      });
    }
    
    if (columnFilters.check_out) {
      const filterDate = columnFilters.check_out;
      result = result.filter(b => {
        const checkOut = b.check_out ? new Date(b.check_out).toISOString().split('T')[0] : '';
        return checkOut.includes(filterDate);
      });
    }
    
    // Helper locale per il nome proprietà (per evitare problemi di scope)
    const getPropName = (booking) => {
      if (booking.property_id) {
        const prop = properties.find(p => p.id === booking.property_id);
        if (prop) return prop.name;
      }
      return booking.property_name || 'N/A';
    };
    
    // Ordinamento
    result.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortConfig.field) {
        case 'check_in':
          aVal = a.check_in ? new Date(a.check_in) : new Date(0);
          bVal = b.check_in ? new Date(b.check_in) : new Date(0);
          break;
        case 'check_out':
          aVal = a.check_out ? new Date(a.check_out) : new Date(0);
          bVal = b.check_out ? new Date(b.check_out) : new Date(0);
          break;
        case 'guest_name':
          aVal = (a.guest_name || '').toLowerCase();
          bVal = (b.guest_name || '').toLowerCase();
          break;
        case 'property':
          aVal = getPropName(a).toLowerCase();
          bVal = getPropName(b).toLowerCase();
          break;
        case 'guests_count':
          aVal = a.guests_count || 0;
          bVal = b.guests_count || 0;
          break;
        default:
          aVal = a.check_in ? new Date(a.check_in) : new Date(0);
          bVal = b.check_in ? new Date(b.check_in) : new Date(0);
      }
      
      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = sortConfig.field.includes('date') || sortConfig.field.includes('check') ? new Date(0) : '';
      if (bVal === null || bVal === undefined) bVal = sortConfig.field.includes('date') || sortConfig.field.includes('check') ? new Date(0) : '';
      
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return result;
  }, [bookings, filters, columnFilters, sortConfig, properties, isExternal]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const resetFilters = () => {
    setFilters({
      year: new Date().getFullYear().toString(),
      month: 'all',
      search: '',
      propertyId: 'all',
      dateFrom: '',
      dateTo: ''
    });
    setColumnFilters({
      guest_name: '',
      property: '',
      check_in: '',
      check_out: ''
    });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-purple-600" />
      : <ArrowDown className="h-4 w-4 text-purple-600" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pt-14 md:pt-6 md:p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <History className="h-6 w-6 text-purple-600" />
            {isExternal ? 'Prenotazioni' : 'Storico Prenotazioni'}
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {filteredAndSortedBookings.length} prenotazioni trovate
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {isExternal && (
            <Button
              className="bg-blue-700 hover:bg-blue-800 text-white"
              onClick={() => setShowNewBooking(true)}
              data-testid="new-booking-btn"
            >
              + Nuova Prenotazione
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
            {(filters.search || filters.propertyId !== 'all' || filters.month !== 'all' || filters.dateFrom || filters.dateTo) && (
              <Badge className="bg-purple-100 text-purple-700 ml-1">Attivi</Badge>
            )}
          </Button>
        </div>
      </div>

      <NewBookingModal
        open={showNewBooking}
        onClose={() => setShowNewBooking(false)}
        properties={properties}
        onSaved={fetchData}
      />

      {cancelled.available && (
        <div className="flex gap-2 border-b" data-testid="bookings-view-tabs">
          {[['active', 'Attive', filteredAndSortedBookings.length], ['cancelled', 'Annullate', cancelled.bookings.length]].map(([key, label, count]) => (
            <button key={key} type="button" onClick={() => setView(key)} data-testid={`bookings-tab-${key}`}
              className={`flex items-center gap-2 border-b-2 px-4 py-2 font-medium transition-colors ${view === key ? 'border-purple-600 text-purple-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {key === 'cancelled' ? <Ban className="h-4 w-4" /> : <History className="h-4 w-4" />}{label}
              <Badge variant="secondary" data-testid={`bookings-tab-${key}-count`}>{count}</Badge>
            </button>
          ))}
        </div>
      )}

      {view === 'cancelled' && cancelled.available && (
        <Card data-testid="cancelled-bookings-card"><CardContent className="p-0">
          <CancelledItems kind="bookings" rows={cancelled.bookings} onChanged={refreshAfterRestore} />
        </CardContent></Card>
      )}
      {(view !== 'cancelled' || !cancelled.available) && <>

      {/* Filtri */}
      {showFilters && (
        <Card className="bg-slate-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Ricerca */}
              <div className="lg:col-span-2">
                <Label className="text-xs text-slate-500">Cerca (nome, telefono, email, codice)</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Cerca..."
                    value={filters.search}
                    onChange={(e) => setFilters(f => ({ ...f, search: e.target.value }))}
                    className="pl-9"
                  />
                </div>
              </div>
              
              {/* Anno */}
              <div>
                <Label className="text-xs text-slate-500">Anno</Label>
                <Select value={filters.year} onValueChange={(v) => setFilters(f => ({ ...f, year: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.map(year => (
                      <SelectItem key={year} value={year}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Mese */}
              <div>
                <Label className="text-xs text-slate-500">Mese</Label>
                <Select value={filters.month} onValueChange={(v) => setFilters(f => ({ ...f, month: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map(m => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Immobile */}
              <div>
                <Label className="text-xs text-slate-500">Immobile</Label>
                <Select value={filters.propertyId} onValueChange={(v) => setFilters(f => ({ ...f, propertyId: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Tutti gli immobili" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tutti gli immobili</SelectItem>
                    {properties.filter(p => p && p.id && p.name).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Data Da */}
              <div>
                <Label className="text-xs text-slate-500">Check-in dal</Label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => setFilters(f => ({ ...f, dateFrom: e.target.value }))}
                />
              </div>
              
              {/* Data A */}
              <div>
                <Label className="text-xs text-slate-500">Check-in al</Label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => setFilters(f => ({ ...f, dateTo: e.target.value }))}
                />
              </div>
              
              {/* Reset */}
              <div className="flex items-end">
                <Button variant="ghost" onClick={resetFilters} className="w-full">
                  <X className="h-4 w-4 mr-1" />
                  Reset filtri
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabella */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('guest_name')}
                  >
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      Ospite
                      <SortIcon field="guest_name" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-left text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('property')}
                  >
                    <div className="flex items-center gap-1">
                      <Building className="h-4 w-4" />
                      Immobile
                      <SortIcon field="property" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('check_in')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Check-in
                      <SortIcon field="check_in" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('check_out')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <Calendar className="h-4 w-4" />
                      Check-out
                      <SortIcon field="check_out" />
                    </div>
                  </th>
                  <th 
                    className="px-4 py-3 text-center text-xs font-semibold text-slate-600 cursor-pointer hover:bg-slate-100"
                    onClick={() => handleSort('guests_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Ospiti
                      <SortIcon field="guests_count" />
                    </div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600">
                    Dettagli
                  </th>
                </tr>
                {/* Riga filtri inline */}
                <tr className="bg-slate-100 border-b">
                  <th className="px-4 py-2">
                    <Input
                      placeholder="Filtra ospite..."
                      value={columnFilters.guest_name}
                      onChange={(e) => setColumnFilters(f => ({ ...f, guest_name: e.target.value }))}
                      className="h-8 text-xs bg-white"
                      data-testid="filter-guest-name"
                    />
                  </th>
                  <th className="px-4 py-2">
                    <Input
                      placeholder="Filtra immobile..."
                      value={columnFilters.property}
                      onChange={(e) => setColumnFilters(f => ({ ...f, property: e.target.value }))}
                      className="h-8 text-xs bg-white"
                      data-testid="filter-property"
                    />
                  </th>
                  <th className="px-4 py-2">
                    <Input
                      type="date"
                      value={columnFilters.check_in}
                      onChange={(e) => setColumnFilters(f => ({ ...f, check_in: e.target.value }))}
                      className="h-8 text-xs bg-white"
                      data-testid="filter-check-in"
                    />
                  </th>
                  <th className="px-4 py-2">
                    <Input
                      type="date"
                      value={columnFilters.check_out}
                      onChange={(e) => setColumnFilters(f => ({ ...f, check_out: e.target.value }))}
                      className="h-8 text-xs bg-white"
                      data-testid="filter-check-out"
                    />
                  </th>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2">
                    {(columnFilters.guest_name || columnFilters.property || columnFilters.check_in || columnFilters.check_out) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setColumnFilters({ guest_name: '', property: '', check_in: '', check_out: '' })}
                        className="h-8 text-xs text-red-600 hover:text-red-700"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Reset
                      </Button>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredAndSortedBookings.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                      <History className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                      <p>Nessuna prenotazione trovata</p>
                      <p className="text-sm">Prova a modificare i filtri</p>
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedBookings.map((booking) => (
                    <React.Fragment key={booking.id}>
                      <tr 
                        className={`hover:bg-slate-50 cursor-pointer ${booking.cancelled ? 'bg-red-50 opacity-75' : ''}`}
                        onClick={() => setExpandedBooking(expandedBooking === booking.id ? null : booking.id)}
                        data-testid={`storico-row-${booking.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className={`font-medium ${booking.cancelled ? 'text-red-600 line-through' : 'text-slate-800'}`}>{booking.guest_name || 'N/A'}</div>
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            {booking.booking_code}
                            {booking.cancelled && <Badge className="bg-red-600 text-white text-xs font-bold ml-1" data-testid={`badge-cancelled-storico-${booking.id}`}>ANNULLATA</Badge>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Home className="h-4 w-4 text-slate-400" />
                            <span className="text-sm notranslate" translate="no">{getPropertyName(booking)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm">{formatDate(booking.check_in)}</td>
                        <td className="px-4 py-3 text-center text-sm">{formatDate(booking.check_out)}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">{booking.guests_count || 1}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button variant="ghost" size="sm">
                            {expandedBooking === booking.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </td>
                      </tr>
                      
                      {/* Dettagli espansi */}
                      {expandedBooking === booking.id && (
                        <tr className="bg-slate-50">
                          <td colSpan={6} className="px-4 py-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Contatti */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-slate-700">Contatti</h4>
                                {(booking.guest_phone || booking.phone) && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Phone className="h-4 w-4 text-slate-400" />
                                    <a href={`tel:${booking.guest_phone || booking.phone}`} className="text-blue-600 hover:underline">
                                      {booking.guest_phone || booking.phone}
                                    </a>
                                  </div>
                                )}
                                {(booking.guest_email || booking.email) && (
                                  <div className="flex items-center gap-2 text-sm">
                                    <Mail className="h-4 w-4 text-slate-400" />
                                    <a href={`mailto:${booking.guest_email || booking.email}`} className="text-blue-600 hover:underline">
                                      {booking.guest_email || booking.email}
                                    </a>
                                  </div>
                                )}
                              </div>
                              
                              {/* Info Soggiorno */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-slate-700">Soggiorno</h4>
                                <div className="text-sm text-slate-600">
                                  <p>Notti: {Math.ceil((new Date(booking.check_out) - new Date(booking.check_in)) / (1000 * 60 * 60 * 24))}</p>
                                  <p>Ospiti: {booking.guests_count || 1} {booking.children_count > 0 && `(${booking.children_count} bambini)`}</p>
                                </div>
                              </div>
                              
                              {/* Timer Questura 24h */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-slate-700">Timer Questura</h4>
                                <QuesturaTimer 
                                  checkInDate={booking.check_in} 
                                  questuraSent={booking.questura_sent}
                                />
                              </div>
                              
                              {/* Stati */}
                              <div className="space-y-2">
                                <h4 className="font-semibold text-sm text-slate-700">Stato</h4>
                                <div className="flex flex-wrap gap-2">
                                  {booking.questura_sent && (
                                    <Badge className="bg-green-100 text-green-700">Questura ✓</Badge>
                                  )}
                                  {booking.receipt_downloaded && (
                                    <Badge className="bg-blue-100 text-blue-700">Ricevuta ✓</Badge>
                                  )}
                                  {booking.checkin_completed && (
                                    <Badge className="bg-purple-100 text-purple-700">Check-in ✓</Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            {cancelled.available && (
                              <div className="mt-4 flex justify-end border-t pt-3">
                                <ConfirmAction testId={`cancel-booking-${booking.id}`} label="Annulla prenotazione" icon={Ban} destructive
                                  className="border-red-200 text-red-700 hover:bg-red-50"
                                  title="Annullare questa prenotazione?"
                                  description={`${booking.guest_name || 'Ospite'} · ${formatDate(booking.check_in)} → ${formatDate(booking.check_out)}. Le date tornano libere e la prenotazione passa nella scheda «Annullate», da cui puoi ripristinarla. All’ospite non viene inviata nessuna email.`}
                                  confirmLabel="Annulla prenotazione" onConfirm={() => cancelBooking(booking)} />
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      
      {/* Riepilogo */}
      <div className="text-center text-sm text-slate-500">
        Totale: {filteredAndSortedBookings.length} prenotazioni | 
        Anno: {filters.year} | 
        Ordine: {sortConfig.field === 'check_in' ? 'Check-in' : 
                 sortConfig.field === 'check_out' ? 'Check-out' : 
                 sortConfig.field === 'guest_name' ? 'Nome' : 
                 sortConfig.field === 'property' ? 'Immobile' : 'Ospiti'} 
        ({sortConfig.direction === 'asc' ? '↑' : '↓'})
      </div>
      </>}
    </div>
  );
};

export default StoricoPrenotazioni;
